/**
 * 語音合成工具，用於朗讀文本
 * 提供日語假名的朗讀功能，並允許自定義語速和音調
 */

/**
 * 朗讀日語假名
 * @param {string} kanaText - 要發音的假名文本
 * @param {Object} options - 可選參數
 * @param {string} options.lang - 語言代碼，默認為日語 (ja-JP)
 * @param {number} options.pitch - 音調，範圍 0-2，默認為 1
 * @param {number} options.rate - 語速，範圍 0.1-10，默認為 1
 * @param {number} options.volume - 音量，範圍 0-1，默認為 1
 * @returns {SpeechSynthesisUtterance} 語音合成對象
 */
export function speakKana(kanaText, options = {}) {
  if (!kanaText) return null;
  
  const {
    lang = "ja-JP",
    pitch = 1,
    rate = 1,
    volume = 1
  } = options;

  // 瀏覽器支持檢查
  if (!window.speechSynthesis) {
    console.warn('當前瀏覽器不支持 Web Speech API');
    return null;
  }

  // 創建語音合成對象
  const utterance = new SpeechSynthesisUtterance(kanaText);
  
  // 設置語音參數
  utterance.lang = lang;
  utterance.pitch = Math.max(0, Math.min(2, pitch)); // 限制在 0-2 範圍內
  utterance.rate = Math.max(0.1, Math.min(10, rate)); // 限制在 0.1-10 範圍內
  utterance.volume = Math.max(0, Math.min(1, volume)); // 限制在 0-1 範圍內
  
  // 朗讀文本
  window.speechSynthesis.speak(utterance);
  
  return utterance;
}

/**
 * 獲取可用的語音合成聲音列表
 * @param {string} lang - 可選，限制特定語言的聲音
 * @returns {SpeechSynthesisVoice[]} 聲音列表
 */
export function getAvailableVoices(lang = null) {
  if (!window.speechSynthesis) {
    console.warn('當前瀏覽器不支持 Web Speech API');
    return [];
  }
  
  const voices = window.speechSynthesis.getVoices();
  
  if (lang) {
    return voices.filter(voice => voice.lang.startsWith(lang));
  }
  
  return voices;
}

/**
 * 使用特定聲音朗讀文本
 * @param {string} text - 要發音的文本
 * @param {SpeechSynthesisVoice} voice - 要使用的聲音
 * @param {Object} options - 其他選項，同 speakKana
 */
export function speakWithVoice(text, voice, options = {}) {
  if (!window.speechSynthesis || !text) return null;
  
  const utterance = new SpeechSynthesisUtterance(text);
  
  if (voice) {
    utterance.voice = voice;
  }
  
  // 設置其他參數
  if (options.pitch !== undefined) utterance.pitch = options.pitch;
  if (options.rate !== undefined) utterance.rate = options.rate;
  if (options.volume !== undefined) utterance.volume = options.volume;
  
  window.speechSynthesis.speak(utterance);
  
  return utterance;
}

/**
 * 停止當前所有語音朗讀
 */
export function stopSpeaking() {
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}

/**
 * 檢查瀏覽器是否支持語音合成
 * @returns {boolean} 是否支持
 */
export function isSpeechSynthesisSupported() {
  return 'speechSynthesis' in window;
}

// 默認導出最常用的功能
export default speakKana;
